import {
  PARTY_PUBLIC_AUTO_START_DELAY_MILLISECONDS,
  isPartyRoomCode,
  normalizePartyRoomCode,
  type PartyPlayerView,
  type PartyPublicRoomSummary,
  type PartyRoomView,
} from './protocol';
export { shareKakaoInvite as sharePartyInvite } from '../kakao-share';

export const PARTY_PLAYER_COLORS = [
  '#ff746d',
  '#45cdc4',
  '#ffd45c',
  '#8e6ee8',
  '#4ca7ef',
  '#f58bb5',
  '#75c858',
  '#ef9b48',
] as const;

export function escapePartyHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[character]!);
}

export function inviteRoomFromLocation(): string | null {
  const value = normalizePartyRoomCode(new URL(window.location.href).searchParams.get('room') || '');
  return isPartyRoomCode(value) ? value : null;
}

export function partyInviteUrl(pathname: string, roomCode: string): string {
  const url = new URL(pathname, window.location.origin);
  url.searchParams.set('room', roomCode);
  return url.toString();
}

export async function copyPartyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    const input = document.createElement('textarea');
    input.value = value;
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.append(input);
    input.select();
    const copied = document.execCommand('copy');
    input.remove();
    return copied;
  }
}

export function partyPlayerMarkup(player: PartyPlayerView, selfId: string | null): string {
  const color = PARTY_PLAYER_COLORS[player.colorIndex % PARTY_PLAYER_COLORS.length];
  const pattern = player.colorIndex >= 6 ? ' patterned' : '';
  return `
    <li class="party-player${player.connected ? '' : ' disconnected'}${player.ready ? ' ready' : ''}">
      <span class="party-bean-avatar${pattern}" style="--party-player-color:${color}" aria-hidden="true"><i></i></span>
      <span class="party-player-name">${escapePartyHtml(player.nickname)}${player.id === selfId ? '<small>나</small>' : ''}</span>
      ${player.isHost ? '<span class="party-player-role">방장</span>' : ''}
      <span class="party-player-state">${player.connected ? (player.ready ? '준비' : '대기') : '재접속 중'}</span>
    </li>`;
}

export function partyPlayersMarkup(room: PartyRoomView, selfId: string | null): string {
  return `<ul class="party-player-list">${room.players.map((player) => partyPlayerMarkup(player, selfId)).join('')}</ul>`;
}

export function savedPartyNickname(): string {
  try {
    return window.localStorage.getItem('party-nickname') || '';
  } catch {
    return '';
  }
}

export function savePartyNickname(value: string): void {
  try {
    window.localStorage.setItem('party-nickname', value);
  } catch {
    // A nickname is convenience-only and works without storage.
  }
}

export interface PartyEntryMarkupOptions {
  readonly incomingRoom: string | null;
  readonly title: string;
  readonly description: string;
  readonly notice: string;
  readonly busy: boolean;
  readonly activeTab: PartyEntryTab;
  readonly nicknameValue: string;
  readonly publicRooms: readonly PartyPublicRoomSummary[];
  readonly publicRoomsLoaded: boolean;
}

export type PartyEntryTab = 'private' | 'public';

function publicRoomMarkup(room: PartyPublicRoomSummary, busy: boolean): string {
  return `
    <li class="party-public-room">
      <span class="party-public-room-icon" aria-hidden="true">●</span>
      <span class="party-public-room-copy">
        <strong>${escapePartyHtml(room.hostNickname)}의 방</strong>
        <small>방 코드 ${room.roomCode}${room.autoStartAt === null ? '' : ' · 시작 준비 중'}</small>
      </span>
      <span class="party-public-room-count">${room.playerCount}/${room.maxPlayers}</span>
      <button class="party-quiet-button" type="submit" data-entry-mode="join-public" data-room-code="${room.roomCode}" ${busy ? 'disabled' : ''}>참가</button>
    </li>`;
}

export function partyEntryMarkup(options: PartyEntryMarkupOptions): string {
  const privateActive = options.activeTab === 'private';
  const nickname = options.nicknameValue;
  const acceptingInvite = Boolean(options.incomingRoom) && privateActive;
  const publicRoomList = options.publicRoomsLoaded
    ? options.publicRooms.length > 0
      ? `<ul class="party-public-room-list">${options.publicRooms.map((room) => publicRoomMarkup(room, options.busy)).join('')}</ul>`
      : '<div class="party-public-empty"><strong>지금 기다리는 방이 없어요</strong><span>빠른 참가를 누르면 첫 공개방이 만들어져요.</span></div>'
    : '<div class="party-public-empty loading"><strong>공개방을 찾는 중이에요</strong><span>잠시만 기다려 주세요.</span></div>';
  return `
    <section class="party-panel center party-entry-panel">
      <span class="party-eyebrow">친구와도 · 새로운 사람과도</span>
      <h2>${acceptingInvite ? '초대받은 방에 들어갈까요?' : escapePartyHtml(options.title)}</h2>
      <p>${escapePartyHtml(options.description)}</p>
      <form id="party-entry-form">
        <label class="party-field">
          <span>내 닉네임 · 최대 10자</span>
          <input id="party-nickname" maxlength="10" autocomplete="nickname" value="${escapePartyHtml(nickname)}" placeholder="예: 파도콩" required />
        </label>
        <div class="party-entry-tabs" role="tablist" aria-label="게임 상대 선택">
          <button type="button" role="tab" aria-selected="${privateActive}" class="${privateActive ? 'active' : ''}" data-party-entry-tab="private">친구와 하기</button>
          <button type="button" role="tab" aria-selected="${!privateActive}" class="${privateActive ? '' : 'active'}" data-party-entry-tab="public">공개 게임 <small>NEW</small></button>
        </div>
        <div class="party-entry-section" role="tabpanel" ${privateActive ? '' : 'hidden'}>
          <button class="party-primary full" type="submit" data-entry-mode="create-private" ${options.busy ? 'disabled' : ''}>비공개 방 만들기</button>
          <div class="party-divider">또는 방 코드로 참가</div>
          <div class="party-join-row">
            <label class="party-field party-code-field">
              <span>6자리 방 코드</span>
              <input id="party-room-code" maxlength="6" autocapitalize="characters" spellcheck="false" value="${escapePartyHtml(options.incomingRoom ?? '')}" placeholder="ABC234" />
            </label>
            <button class="party-primary blue" type="submit" data-entry-mode="join-private" ${options.busy ? 'disabled' : ''}>참가하기</button>
          </div>
        </div>
        <div class="party-entry-section party-public-section" role="tabpanel" ${privateActive ? 'hidden' : ''}>
          <button class="party-primary full blue party-quick-join" type="submit" data-entry-mode="quick-public" ${options.busy ? 'disabled' : ''}>
            <span>빠른 참가</span><small>참가할 방이 없으면 새 방을 만들어요</small>
          </button>
          <button class="party-public-create" type="submit" data-entry-mode="create-public" ${options.busy ? 'disabled' : ''}>내가 공개방 만들기</button>
          <div class="party-public-list-heading">
            <strong>참가 가능한 방 ${options.publicRoomsLoaded ? options.publicRooms.length : ''}</strong>
            <button type="button" data-party-action="refresh-public" ${options.busy ? 'disabled' : ''}>새로고침</button>
          </div>
          ${publicRoomList}
        </div>
      </form>
      <p class="party-notice" role="status">${escapePartyHtml(options.notice)}</p>
    </section>`;
}

export interface PartyLobbyMarkupOptions {
  readonly room: PartyRoomView;
  readonly selfId: string | null;
  readonly gameLabel: string;
  readonly startLabel: string;
  readonly notice: string;
}

export function partyLobbyMarkup(options: PartyLobbyMarkupOptions): string {
  const { room, selfId } = options;
  const self = room.players.find((player) => player.id === selfId);
  const isHost = self?.isHost ?? false;
  const canStart = room.players.length >= room.minPlayers
    && room.players.every((player) => player.connected && player.ready);
  const missing = Math.max(0, room.minPlayers - room.players.length);
  const isPublic = room.visibility === 'public';
  const initialAutoStartSeconds = room.autoStartAt === null
    ? null
    : Math.max(0, Math.ceil((room.autoStartAt - room.serverTime) / 1_000));
  return `
    <section class="party-panel center party-lobby-panel">
      <span class="party-eyebrow">${isPublic ? '공개 게임' : escapePartyHtml(options.gameLabel)} 대기방 · ${room.players.length}/${room.maxPlayers}</span>
      <h2>${missing > 0 ? `${missing}명만 더 오면 시작!` : isPublic ? '모두 준비하면 곧 출발!' : '친구들이 준비하고 있어요'}</h2>
      <p>${isPublic ? '공개 목록에서 참가할 수 있어요. 링크로 친구를 초대해도 좋아요.' : '아래 링크나 방 코드를 보내세요. 참가자는 준비 버튼을 눌러주세요.'}</p>
      <div class="party-room-code" aria-label="방 코드 ${room.roomCode}">${room.roomCode}</div>
      <div class="party-invite-actions">
        <button class="party-quiet-button" type="button" data-party-action="copy-invite">초대 링크 복사</button>
        <button class="party-quiet-button" type="button" data-party-action="share-invite">카카오톡으로 초대</button>
      </div>
      ${partyPlayersMarkup(room, selfId)}
      ${isPublic ? `<div id="party-auto-start" class="party-auto-start ${room.autoStartAt === null ? '' : 'counting'}">${initialAutoStartSeconds === null ? `모두 준비하면 ${PARTY_PUBLIC_AUTO_START_DELAY_MILLISECONDS / 1_000}초 뒤 자동 시작해요` : `${initialAutoStartSeconds}초 뒤 자동 시작해요`}</div>` : ''}
      ${isHost ? `
        <button class="party-primary full coral" type="button" data-party-action="start" ${canStart ? '' : 'disabled'}>
          ${canStart ? escapePartyHtml(options.startLabel) : room.players.length < room.minPlayers ? `최소 ${room.minPlayers}명이 필요해요` : '모두 준비하면 시작할 수 있어요'}
        </button>` : `
        <button class="party-primary full ${self?.ready ? 'sun' : ''}" type="button" data-party-action="ready">
          ${self?.ready ? '준비 취소' : '준비 완료!'}
        </button>`}
      <button class="party-leave-button" type="button" data-party-action="leave">방 나가기</button>
      <p class="party-notice" role="status">${escapePartyHtml(options.notice)}</p>
    </section>`;
}

export function updatePartyLobbyAutoStart(
  root: ParentNode,
  room: PartyRoomView,
  serverNow: number,
): void {
  if (room.visibility !== 'public') return;
  const label = root.querySelector<HTMLElement>('#party-auto-start');
  if (!label) return;
  if (room.autoStartAt === null) {
    label.classList.remove('counting');
    label.textContent = `모두 준비하면 ${PARTY_PUBLIC_AUTO_START_DELAY_MILLISECONDS / 1_000}초 뒤 자동 시작해요`;
    return;
  }
  const seconds = Math.max(0, Math.ceil((room.autoStartAt - serverNow) / 1_000));
  label.classList.add('counting');
  label.textContent = seconds > 0 ? `${seconds}초 뒤 자동 시작해요` : '게임을 시작하고 있어요';
}
